import { useEffect, useState } from "react";
import {
  MapContainer,
  Polyline,
  Marker,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import {
  Box,
  Button,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Paper,
} from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-geometryutil";
import { listSegments, createRoute } from "../../api/route";
import { Save, Undo, Redo } from "@mui/icons-material";

// ✅ icons
const dotIcon = L.divIcon({
  html: `<div style="width:12px;height:12px;background:blue;border:2px solid white;border-radius:50%;"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});
const dotIconSelected = L.divIcon({
  html: `<div style="width:16px;height:16px;background:red;border:2px solid yellow;border-radius:50%;"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// ✅ handle click on map
function MapClickHandler({ mode, segments, addPointOnLine, addPointAnywhere }: any) {
  const map = useMapEvents({
    click(e) {
      if (mode !== "add") return;
      const click = L.latLng(e.latlng.lat, e.latlng.lng);
      let nearestSeg: number | null = null;
      let minDist = Infinity;
      segments.forEach((seg: any, i: number) => {
        const latlngs = seg.polyline.map((p: any) => L.latLng(p.lat, p.lng));
        if (latlngs.length < 2) return;
        const closest = (L as any).GeometryUtil.closest(map, latlngs, click);
        if (closest) {
          const dist = (L as any).GeometryUtil.distance(map, click, closest);
          if (dist < minDist) {
            minDist = dist;
            nearestSeg = i;
          }
        }
      });
      if (nearestSeg !== null && minDist < 25)
        addPointOnLine(nearestSeg, [e.latlng.lat, e.latlng.lng], map);
      else addPointAnywhere([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export default function RouteCreatePage() {
  const [segments, setSegments] = useState<any[]>([]);
  const [history, setHistory] = useState<any[][]>([]);
  const [future, setFuture] = useState<any[][]>([]);
  const [mode, setMode] = useState<"delete" | "add" | null>(null);
  const [selected, setSelected] = useState<{ segIndex: number; pointIndex: number } | null>(null);
  const [name, setName] = useState("");

  // โหลด segment เริ่มต้น
  useEffect(() => {
    (async () => {
      try {
        const data = await listSegments();
        setSegments(data);
      } catch (err) {
        console.error("โหลด segments ไม่สำเร็จ:", err);
      }
    })();
  }, []);

  // ✅ utility: push state ลง history ก่อนแก้
  const pushHistory = (newSegs: any[]) => {
    setHistory((prev) => [...prev, segments]);
    setSegments(newSegs);
    setFuture([]); // reset redo
  };

  // ✅ undo
  const handleUndo = () => {
    if (history.length === 0) return;
    const prevState = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setFuture((f) => [segments, ...f]);
    setSegments(prevState);
  };

  // ✅ redo
  const handleRedo = () => {
    if (future.length === 0) return;
    const nextState = future[0];
    setFuture((f) => f.slice(1));
    setHistory((h) => [...h, segments]);
    setSegments(nextState);
  };

  // ✅ อัพเดตตำแหน่ง marker ที่ถูกลาก
  const updatePoint = (segIndex: number, pointIndex: number, newPos: [number, number]) => {
    const newSegs = segments.map((seg, i) =>
      i === segIndex
        ? {
            ...seg,
            polyline: seg.polyline.map((p: any, j: number) =>
              j === pointIndex ? { lat: newPos[0], lng: newPos[1] } : p
            ),
          }
        : seg
    );
    pushHistory(newSegs);
  };

  // ✅ ลบจุด
  const deletePoint = (segIndex: number, pointIndex: number) => {
    const newSegs = segments.map((seg, i) =>
      i === segIndex
        ? { ...seg, polyline: seg.polyline.filter((_: any, j: number) => j !== pointIndex) }
        : seg
    );
    pushHistory(newSegs);
    setSelected(null);
  };

  // ✅ เพิ่มจุดบนเส้น
  const addPointOnLine = (segIndex: number, clickLatLng: [number, number], map: L.Map) => {
    const newSegs = segments.map((seg, i) => {
      if (i !== segIndex) return seg;
      const poly = seg.polyline;
      if (poly.length < 2) return seg;
      let minDist = Infinity;
      let insertIndex = 0;
      for (let j = 0; j < poly.length - 1; j++) {
        const a = L.latLng(poly[j].lat, poly[j].lng);
        const b = L.latLng(poly[j + 1].lat, poly[j + 1].lng);
        const p = L.latLng(clickLatLng[0], clickLatLng[1]);
        const dist = (L as any).GeometryUtil.distanceSegment(map, p, a, b);
        if (dist < minDist) {
          minDist = dist;
          insertIndex = j + 1;
        }
      }
      const newPolyline = [
        ...poly.slice(0, insertIndex),
        { lat: clickLatLng[0], lng: clickLatLng[1] },
        ...poly.slice(insertIndex),
      ];
      return { ...seg, polyline: newPolyline };
    });
    pushHistory(newSegs);
  };

  // ✅ เพิ่มจุดใหม่ (นอกเส้น)
  const addPointAnywhere = (clickLatLng: [number, number]) => {
    let newSegs: any[];
    if (segments.length === 0) {
      newSegs = [
        {
          fromStationId: 0,
          toStationId: 0,
          polyline: [{ lat: clickLatLng[0], lng: clickLatLng[1] }],
        },
      ];
    } else {
      newSegs = segments.map((seg, i) =>
        i === 0
          ? {
              ...seg,
              polyline: [...seg.polyline, { lat: clickLatLng[0], lng: clickLatLng[1] }],
            }
          : seg
      );
    }
    pushHistory(newSegs);
  };

  const handleSave = async () => {
    try {
      await createRoute({ name, segments });
      alert("✅ บันทึกเส้นทางสำเร็จ");
    } catch (err: any) {
      alert("❌ บันทึกไม่สำเร็จ: " + err.message);
    }
  };

  return (
    <Stack direction="row" spacing={2} sx={{ height: "100vh" }}>
      <Box sx={{ width: 320, p: 2 }}>
        <Typography variant="h6">สร้างเส้นทางใหม่</Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="ชื่อเส้นทาง"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Paper>

        <Typography variant="subtitle1">เครื่องมือ</Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, value) => setMode(value)}
          fullWidth
          sx={{ mb: 1 }}
        >
          <ToggleButton value="add">➕ เพิ่มจุด</ToggleButton>
          <ToggleButton value="delete">🗑 ลบจุด</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Undo />}
            onClick={handleUndo}
            disabled={history.length === 0}
            fullWidth
          >
            Undo
          </Button>
          <Button
            variant="outlined"
            startIcon={<Redo />}
            onClick={handleRedo}
            disabled={future.length === 0}
            fullWidth
          >
            Redo
          </Button>
        </Stack>

        <Button variant="contained" startIcon={<Save />} fullWidth onClick={handleSave}>
          บันทึก
        </Button>
      </Box>

      <Box sx={{ flex: 1 }}>
        <MapContainer center={[18.7487, 98.9253]} zoom={15} style={{ height: "100vh", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapClickHandler
            mode={mode}
            segments={segments}
            addPointOnLine={addPointOnLine}
            addPointAnywhere={addPointAnywhere}
          />

          {segments.map((seg, segIndex) => {
            const positions = seg.polyline.map((p: any) => [p.lat, p.lng] as [number, number]);
            return (
              <div key={segIndex}>
                <Polyline positions={positions} color="blue" />
                {positions.map((pos : any, pointIndex : any) => (
                  <Marker
                    key={pointIndex}
                    position={pos}
                    icon={
                      selected?.segIndex === segIndex &&
                      selected?.pointIndex === pointIndex
                        ? dotIconSelected
                        : dotIcon
                    }
                    draggable
                    eventHandlers={{
                      dragend: (e) => {
                        const newLatLng = (e.target as L.Marker).getLatLng();
                        updatePoint(segIndex, pointIndex, [newLatLng.lat, newLatLng.lng]);
                      },
                      click: () => {
                        if (mode === "delete") deletePoint(segIndex, pointIndex);
                        else setSelected({ segIndex, pointIndex });
                      },
                    }}
                  />
                ))}
              </div>
            );
          })}
        </MapContainer>
      </Box>
    </Stack>
  );
}
